import { inject, Injectable } from '@angular/core';
import { NotificationType } from '@hunting-nature/shared-core/enums/push-notification-event-type.enum';
import type { PushDeviceRegistration } from '@hunting-nature/shared-core/interfaces/push-device-registration.interface';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  concat,
  concatMap,
  EMPTY,
  endWith,
  exhaustMap,
  filter,
  from,
  map,
  mergeMap,
  of,
  retry,
  switchMap,
  tap,
  throwError,
  timeout,
  timer,
  withLatestFrom,
} from 'rxjs';

import { AppConfigLoader } from '../../../config/app-config.loader';
import { AlbumActions } from '../../../pages/gallery/albums/redux/album.actions';
import { parsePushActionPayload, parsePushNotificationPayload } from '../../helper/push-notification.helper';
import { PushNotificationService } from '../../services/push-notification.service';
import { RestService } from '../../services/rest-service';
import { SettingsService } from '../../services/settings.service';
import { GeneralActions } from '../general/general.actions';
import { GeneralSelectors } from '../general/general.reducer';

import { PushActions } from './push.actions';
import { PushSelectors } from './push.reducer';

/** Extra attempts after the first, and the base of the exponential backoff between them. */
const REGISTRATION_RETRY_COUNT = 3;
const REGISTRATION_RETRY_BASE_DELAY_MS = 1000;

/** Upper bound for the hub cleanup before logout proceeds — offline must not block signing out. */
const UNREGISTER_TIMEOUT_MS = 5000;

/**
 * Only failures a second attempt could plausibly fix. A 4xx means the request itself is
 * wrong — an expired bearer token, a body the contract rejects — so retrying it would just
 * delay `registerDeviceFailed` without changing the outcome.
 */
function isRetryableRegistrationError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') {
    return false;
  }

  // 0 means the request never reached the server at all: offline, DNS, TLS.
  return status === 0 || status >= 500;
}

@Injectable()
export class PushEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly restService = inject(RestService);
  private readonly settingsService = inject(SettingsService);
  private readonly appConfigLoader = inject(AppConfigLoader);
  private readonly log = createLogger('PushEffects');

  /**
   * Push bootstraps after login, not from the app initializer: registering the handle is
   * an authenticated call, and the initializer runs before any token exists.
   *
   * loginSucceeded also fires on a plain app start without a session (isAuthenticated
   * false) — that must neither prompt for permission nor try an unauthenticated register.
   */
  bootstrapOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.loginSucceeded),
      filter(action => action.loginResponse?.isAuthenticated === true),
      map(() => PushActions.initPush())
    )
  );

  /**
   * Re-runs registration when the app comes back to the foreground — Android rotates the
   * FCM token silently, and the only way to notice is to ask again.
   *
   * Runs for a decided permission in *either* direction, because the user can change it in
   * the system settings while the app is backgrounded — including right after a Settings
   * screen sent them there. Without the 'denied' case the store would keep claiming
   * "denied" after the user just switched notifications back on.
   *
   * Nobody is nagged by this: `enable$()` only calls `requestPermissions()` for a 'prompt'
   * state, so a denied permission is re-read and never re-asked. 'unknown' stays out — that
   * one has not been asked yet and would surface a prompt on a plain resume.
   */
  refreshOnResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.appResumed),
      withLatestFrom(this.store.select(PushSelectors.permission), this.store.select(GeneralSelectors.userEMail)),
      // The permission survives a logout in the store; without a user there is nobody to register.
      filter(([, permission, userEmail]) => Boolean(userEmail) && (permission === 'granted' || permission === 'denied')),
      map(() => PushActions.initPush())
    )
  );

  /**
   * Re-syncs device registration when the user changes the sound preference in settings.
   * Ensures the backend receives the updated preference immediately.
   *
   * Skipped if no token has been issued yet.
   */
  resyncDeviceOnSoundPreferenceChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PushActions.soundPreferenceChanged),
      withLatestFrom(
        this.store.select(PushSelectors.installationId),
        this.store.select(PushSelectors.token),
        this.store.select(GeneralSelectors.activeAppLanguage)
      ),
      filter(([, installationId, token]) => Boolean(installationId) && Boolean(token)),
      concatMap(([action, installationId, token, language]) => {
        const platform = this.pushNotificationService.platform;
        if (!platform) {
          return EMPTY;
        }

        const registration: PushDeviceRegistration = {
          deviceId: installationId!,
          platform,
          deviceToken: token!,
          lang: language,
          sound: action.sound,
          staging: this.appConfigLoader.activeConfig().push.staging,
        };

        return this.restService.registerPushDevice$(registration).pipe(
          map(() => PushActions.registerDeviceSucceeded({ installationId: installationId!, token: token! })),
          catchError((error: unknown) => {
            this.log.error('Resync device after preference change failed', { error });
            return of(PushActions.registerDeviceFailed({ error }));
          })
        );
      })
    )
  );

  initPush$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PushActions.initPush),
      switchMap(() => {
        if (!this.pushNotificationService.isSupported) {
          return of(PushActions.initPushSkipped({ reason: 'unsupported' }));
        }
        if (!this.appConfigLoader.activeConfig().push.enabled) {
          return of(PushActions.initPushSkipped({ reason: 'disabled' }));
        }

        return from(this.settingsService.getOrCreatePushInstallationId$()).pipe(
          concatMap(installationId =>
            // concat, not merge: installationIdResolved has to be dispatched and reduced
            // *before* enable$() calls register(), because the OS can hand back a token
            // immediately and registerDevice$ drops tokens that arrive without an id.
            concat(
              of(PushActions.installationIdResolved({ installationId })),
              this.pushNotificationService
                .enable$()
                .pipe(map(result => (result === 'granted' ? PushActions.permissionGranted() : PushActions.permissionDenied())))
            )
          ),
          catchError((error: unknown) => {
            this.log.error('initPush failed', { error });
            return of(PushActions.tokenFailed({ error }));
          })
        );
      })
    )
  );

  /**
   * Plugin-driven, not action-driven: the OS decides when a token appears, and it can
   * appear again at any time when it rotates.
   */
  tokenReceived$ = createEffect(() => this.pushNotificationService.token$.pipe(map(token => PushActions.tokenReceived({ token }))));

  registrationError$ = createEffect(() => this.pushNotificationService.registrationError$.pipe(map(error => PushActions.tokenFailed({ error }))));

  notificationReceived$ = createEffect(() =>
    this.pushNotificationService.notificationReceived$.pipe(
      map(notification => PushActions.notificationReceived({ notification, payload: parsePushNotificationPayload(notification) }))
    )
  );

  notificationActionPerformed$ = createEffect(() =>
    this.pushNotificationService.notificationActionPerformed$.pipe(
      map(action => PushActions.notificationActionPerformed({ action, payload: parsePushActionPayload(action) }))
    )
  );

  handleNotificationTap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PushActions.notificationActionPerformed),
      filter(action => action.action.actionId === 'tap' && action.payload !== null),
      withLatestFrom(
        this.store.select(GeneralSelectors.userEMail),
        this.store.select(GeneralSelectors.loggingIn),
        this.store.select(GeneralSelectors.loggingOut)
      ),
      filter(([, userEmail, loggingIn, loggingOut]) => Boolean(userEmail) && !loggingIn && !loggingOut),
      mergeMap(([action]) => from(this.buildNotificationNavigationActions(action.payload!)))
    )
  );

  consumePendingNotificationIntentOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.loginSucceeded),
      withLatestFrom(this.store.select(PushSelectors.pendingNotificationIntent)),
      mergeMap(([, payload]) => {
        if (!payload) {
          return EMPTY;
        }

        return from(this.buildNotificationNavigationActions(payload));
      })
    )
  );

  /**
   * Hands the fresh handle to the backend, which owns the hub credentials and performs
   * the installation upsert.
   *
   * Skipped when the very same token is already registered — resume fires this on every
   * foreground and the handle usually has not changed.
   */
  registerDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PushActions.tokenReceived),
      withLatestFrom(
        this.store.select(PushSelectors.installationId),
        this.store.select(PushSelectors.isRegisteredWithBackend),
        this.store.select(GeneralSelectors.activeAppLanguage)
      ),
      filter(([, installationId, isRegistered]) => {
        if (!installationId) {
          this.log.warn('Token arrived before the installation id was resolved — skipping');
          return false;
        }

        // Rotation is detected by the reducer, not here: it runs before this effect and
        // clears isRegisteredWithBackend whenever the incoming token differs from the
        // stored one. Comparing the store's token against the action would always match,
        // because the reducer has already written the new value.
        return !isRegistered;
      }),
      concatMap(([action, installationId, , language]) => {
        const platform = this.pushNotificationService.platform;
        if (!platform) {
          return of(PushActions.initPushSkipped({ reason: 'unsupported' }));
        }

        return from(this.settingsService.getPushSoundPreference$()).pipe(
          concatMap(sound => {
            const registration: PushDeviceRegistration = {
              deviceId: installationId!,
              platform,
              deviceToken: action.token,
              lang: language,
              sound,
              staging: this.appConfigLoader.activeConfig().push.staging,
            };

            return this.restService.registerPushDevice$(registration).pipe(
              retry({
                count: REGISTRATION_RETRY_COUNT,
                delay: (error: unknown, retryCount: number) =>
                  isRetryableRegistrationError(error) ? timer(REGISTRATION_RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1)) : throwError(() => error),
              }),
              map(() => PushActions.registerDeviceSucceeded({ installationId: installationId!, token: action.token })),
              catchError((error: unknown) => {
                // RestService already logs each individual attempt; this marks the point
                // where the app stops trying, which is otherwise invisible.
                this.log.error('Register device failed — giving up', { error });
                return of(PushActions.registerDeviceFailed({ error }));
              })
            );
          })
        );
      })
    )
  );

  /**
   * Removes this install from the hub *before* the actual logout. GeneralEffects.logout$
   * clears the OIDC tokens synchronously on native, so a DELETE issued on `logout` itself
   * would already go out without a bearer token — and the device would keep receiving the
   * previous user's notifications until the next login.
   *
   * Logout always follows, whatever the outcome: a failed or timed-out delete must not keep
   * the user signed in. dropOsRegistrationOnLogout$ still invalidates the OS handle, so a
   * leftover hub record stops being deliverable on its first send.
   */
  unregisterBeforeLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GeneralActions.logoutRequested),
      withLatestFrom(this.store.select(PushSelectors.installationId)),
      exhaustMap(([, installationId]) => {
        if (installationId === null) {
          return of(GeneralActions.logout());
        }

        return this.restService.unregisterPushDevice$(installationId, this.appConfigLoader.activeConfig().push.staging).pipe(
          timeout({ first: UNREGISTER_TIMEOUT_MS }),
          map(() => PushActions.unregisterDeviceSucceeded()),
          catchError((error: unknown) => of(PushActions.unregisterDeviceFailed({ error }))),
          endWith(GeneralActions.logout())
        );
      })
    )
  );

  dropOsRegistrationOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PushActions.unregisterDeviceSucceeded, PushActions.unregisterDeviceFailed),
        switchMap(() => this.pushNotificationService.unregister$()),
        switchMap(() => this.pushNotificationService.removeAllDelivered$())
      ),
    { dispatch: false }
  );

  logSkip$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PushActions.initPushSkipped),
        tap(action => this.log.info('Push not started', { reason: action.reason }))
      ),
    { dispatch: false }
  );

  private buildNotificationNavigationActions(
    payload: NonNullable<ReturnType<typeof parsePushNotificationPayload>>
  ): Array<
    | ReturnType<typeof PushActions.clearPendingNotificationIntent>
    | ReturnType<typeof AlbumActions.openAlbum>
    | ReturnType<typeof AlbumActions.openImageViewer>
    | ReturnType<typeof GeneralActions.goToCameraDetails>
  > {
    const actions: Array<
      | ReturnType<typeof PushActions.clearPendingNotificationIntent>
      | ReturnType<typeof AlbumActions.openAlbum>
      | ReturnType<typeof AlbumActions.openImageViewer>
      | ReturnType<typeof GeneralActions.goToCameraDetails>
    > = [];

    if (payload.data?.notificationType === NotificationType.MEDIA_NEW_IMAGE) {
      actions.push(AlbumActions.openAlbum({ albumId: payload.data.imei }));
      actions.push(AlbumActions.openImageViewer({ imageId: payload.data.id }));
    } else if (payload.data?.notificationType === NotificationType.CAMERA_STATUS) {
      actions.push(
        GeneralActions.goToCameraDetails({
          cameraId: payload.data.camera_id,
          cameraName: '',
          model: undefined,
        })
      );
    }

    actions.push(PushActions.clearPendingNotificationIntent());
    return actions;
  }
}
